import React from 'react';

import { DefaultContext } from '@apollo/client';

import { Nullable } from '@clearverify/types';

import {
  EmployerInfoFragment,
  GetRevenueShareSettingsListWithStripeStatusesDocument,
  QueryMode,
  RevenueShareSettingUpdate,
  RevenueShareSettingUpdateMany,
  RevenueShareSettingWhere,
  useGetRevenueShareSettingsListWithStripeStatusesQuery,
  useRevenueShareSettingBulkUpdateMutation,
  useRevenueShareSettingUpdateMutation,
} from 'src/api';
import { FilterKeys } from 'src/constants/filter';
import { useFilterStorage, useSearchQueryVariables } from 'src/hooks/common';
import { useCurrentUserStaff } from 'src/hooks/user';
import { buildPlatformStaffVisibilityByEmployerFilter } from 'src/utils/filters';
import { gql } from 'src/utils/gql';

export const useEmployerBillingList = () => {
  const searchQueryVariables = useSearchQueryVariables(prepareSearchFilter);
  const { sortQueryVariable, getPaginationQueryVariablesByKey, filter } = useFilterStorage();

  const paginationQueryVariables = getPaginationQueryVariablesByKey(
    FilterKeys.EmployersBillingTable,
  );

  const { platformStaff } = useCurrentUserStaff();

  const filterQueryVariables = React.useMemo(() => {
    const employer = filter.employer as Nullable<EmployerInfoFragment>;
    const employerFilter = employer?.id
      ? {
          employerId: { equals: employer.id },
        }
      : {};

    return gql.wrapToAND([
      employerFilter,
      buildPlatformStaffVisibilityByEmployerFilter(platformStaff),
    ]);
  }, [filter.employer, platformStaff]);

  const [employerBillingUpdateMutation, { loading: itemUpdating }] =
    useRevenueShareSettingUpdateMutation();

  const [employerBillingUpdateByFilterMutation, { loading: listUpdating }] =
    useRevenueShareSettingBulkUpdateMutation();

  const { data, previousData, loading } = useGetRevenueShareSettingsListWithStripeStatusesQuery({
    variables: {
      filter: {
        ...searchQueryVariables,
        ...filterQueryVariables,
      },
      ...paginationQueryVariables,
      orderBy: sortQueryVariable,
    },
    notifyOnNetworkStatusChange: true,
  });

  const updateEmployerBilling = React.useCallback(
    async (params: { id: string; data: RevenueShareSettingUpdate; context?: DefaultContext }) => {
      const { id, data, context } = params;
      await employerBillingUpdateMutation({
        variables: {
          where: { id },
          data,
        },
        refetchQueries: [GetRevenueShareSettingsListWithStripeStatusesDocument],
        context,
      });
    },
    [employerBillingUpdateMutation],
  );

  const bulkUpdateEmployerBilling = React.useCallback(
    async (params: {
      data: RevenueShareSettingUpdateMany;
      filter: RevenueShareSettingWhere;
      context?: DefaultContext;
    }) => {
      const { data, context, filter } = params;
      await employerBillingUpdateByFilterMutation({
        variables: {
          data: {
            ...data,
          },
          filter: {
            ...filter,
          },
        },
        context,
      });
    },
    [employerBillingUpdateByFilterMutation],
  );

  const employerBillings = React.useMemo(
    () =>
      (loading
        ? previousData?.getRevenueShareSettingsListWithStripeStatuses.items
        : data?.getRevenueShareSettingsListWithStripeStatuses.items
      )?.map(employerBilling => ({
        id: employerBilling.revenueShareSetting.id,
        revenueShareSetting: employerBilling.revenueShareSetting,
        stripeStatus: employerBilling.stripeStatus,
      })),
    [data, previousData, loading],
  );

  return {
    employerBillingsListCount: data?.getRevenueShareSettingsListWithStripeStatuses.count || 0,
    employerBillings,
    updateEmployerBilling,
    bulkUpdateEmployerBilling,
    loading,
    itemUpdating,
    listUpdating,
  };
};

const prepareSearchFilter = (searchValue: Nullable<string>): RevenueShareSettingWhere =>
  searchValue && searchValue.length > 0
    ? {
        employer: {
          displayName: {
            contains: searchValue,
            mode: QueryMode.Insensitive,
          },
        },
      }
    : {};
